import type { UploadPromoProductsErrorModel } from '@/shared/generated'
import type { AxiosError } from 'axios'
import type {
	PromoCampaignErrorRow,
	PromoCampaignImportFileProps,
} from '../model/types'

import { useState } from 'react'
import { useQueryClient } from '@tanstack/react-query'
import { pickAll } from 'lodash/fp'
import { useTranslation } from 'react-i18next'
import { toast } from 'sonner'

import { client } from '@/shared/api/client'
import { ampli } from '@/shared/lib/ampli'
import Sentry from '@/shared/lib/sentry'
import { errorMessage } from '@/shared/tools/message'

export function useFileDropped({ id, onDone }: PromoCampaignImportFileProps) {
	const { t } = useTranslation()

	const [isLoading, setIsLoading] = useState(false)
	const [errors, setErrors] = useState<Record<
		string,
		PromoCampaignErrorRow[]
	> | null>(null)
	const queryClient = useQueryClient()
	const handleFileDropped = (files: File[]) => {
		ampli.promoImportFile()
		setIsLoading(true)
		setErrors(null)
		client.promoCampaigns
			.uploadPromoCampaignProducts(
				{ promoCampaignId: id },
				{
					file: files[0],
				}
			)
			.then(() => {
				queryClient.invalidateQueries({
					queryKey: ['promo-products', id],
				})
				onDone()
			})
			.catch((error: AxiosError<UploadPromoProductsErrorModel>) => {
				Sentry.captureException(error)
				toast.error(error.message || errorMessage(t), {
					duration: 5000,
				})
				setErrors(
					pickAll(
						[
							'duplicated_rows_errors',
							'multiple_discounts_errors',
							'validation_errors',
						],
						error?.response?.data?.detail
					) as Record<string, PromoCampaignErrorRow[]>
				)
			})
			.finally(() => setIsLoading(false))
	}

	return {
		errors,
		isLoading,
		handleFileDropped,
	}
}
